import React from 'react';

import { useEffect, useState, useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { Checkbox } from 'primereact/checkbox';

import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
// import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import { MultiSelect } from 'primereact/multiselect';
import { Badge } from 'primereact/badge';
import { Modal, Input, Button as AntButton } from 'antd'; // Import Ant Design components


import { fetchLicensesForUserId, getAllLicenses, getLicServers, deleteLicensesFromDatabase,
        rereadLicServer, syncOptFileToLicServer, disableLicenses, enableLicenses,
        requestLicenseExtension, getLicenseHistory, getUserIdWithMultipleEmails,
        addLicServer, updateLicServer, deleteLicServer,
        processLicenseFile, applyLicenseFile
             } from '../../services/api';
import api from '../../services/api';
import { LicenseTransactionEntity, LicServer } from '../../types';
import { clear } from 'console';
import eventBus from '../../eventBus';
import { useSidebar } from '../../contexts/SidebarContext';
import LicenseStatus from './LicenseStatus';
import { getAvailableActions, formatLicenseForDisplay } from '../../utils/licenseStates';
import { useTranslation } from '../../locales/useTranslation';
import { useAuthGuard } from '../../hooks/useAuthGuard';
import { useNavigate } from 'react-router-dom';
import { useNewGui } from '../../hooks/useNewGui';
import PageHeader from '../shared/PageHeader';


/*
NOTES:
Demonstration GUI REFERENCE
Current GUI implementation demonstrates few features of the library
- DataTable with pagination
- DataTable with sorting
- DataTable with filtering
- DataTable with cell editing
- DataTable with row editing
- DataTable with row selection

Few of the features are conflicting ones and will be cleaned up 
Example: Row Selection vs Cell Editing
A click on an editable column will cause corresponding row to be selected / de-selected as well based on present status
=> So Column Editing may be dropped!
However, this demonstration release can be used later to implement cell editing functionality as per need

*/

const LicenseManagementPage: React.FC = () => {
    const navigate = useNavigate();
    const globalFilterFields = useMemo(() => ['id', 'userId', 'email', 'serverInfo', 'toolName', 'licenseCount', 'startDate', 'endDate', 'status', 'source'], []);
    // license is of type LicenseTransactionEntity, and addon attribute 'selected' is added to it (by default it will be missing from the API response)
    const [licenseServers, setLicenseServers] = useState<LicServer[]>([]);
    const [licenses, setLicenses] = useState<LicenseTransactionEntity[]>([]);
    const { isSidebarOpen, toggleSidebar } = useSidebar();
    const useNew = useNewGui();
    const t = useTranslation();
    const { isAuthenticated, authValidated } = useAuthGuard();

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [operationType, setOperationType] = useState<string | null>(null);
    const [remarks, setRemarks] = useState<string>('');
    const [statusMessage, setStatusMessage] = useState<string | null>(null);
    const [statusColor, setStatusColor] = useState<'blue' | 'red' | 'orange' | null>(null);
    const [operationResults, setOperationResults] = useState<string | null>(null);

    // Extension request dialog state
    const [extensionDialogVisible, setExtensionDialogVisible] = useState(false);
    const [extensionDate, setExtensionDate] = useState<Date | null>(null);
    const [extensionRemarks, setExtensionRemarks] = useState<string>('');

    // License History Modal states
    const [showLicenseHistory, setShowLicenseHistory] = useState(false);
    const [selectedLicenseHistory, setSelectedLicenseHistory] = useState<any[]>([]);
    const [selectedLicenseSummary, setSelectedLicenseSummary] = useState<any>(null);

    // Loading state for modal operations
    const [modalLoading, setModalLoading] = useState(false);

    // Loading state for bulk operations
    const [bulkOperationInProgress, setBulkOperationInProgress] = useState(false);
    const [bulkOperationMessage, setBulkOperationMessage] = useState('');

    // User-ids linked to more than one email (for warning highlight)
    const [userIdsWithMultipleEmails, setUserIdsWithMultipleEmails] = useState<string[]>([]);

    // Tab navigation
    const [activeTab, setActiveTab] = useState<'records' | 'serverConfig' | 'licUpdate'>('records');

    // License Server Config tab state
    const [licServerDialogVisible, setLicServerDialogVisible] = useState(false);
    const [editingServer, setEditingServer] = useState<import('../../types').LicServer | null>(null);
    const [deleteServerDialogVisible, setDeleteServerDialogVisible] = useState(false);
    const [serverToDelete, setServerToDelete] = useState<import('../../types').LicServer | null>(null);
    const [serverConfigLoading, setServerConfigLoading] = useState(false);
    const [licServerForm, setLicServerForm] = useState({
        serverFqdn: '', adminUserid: '', sshPort: 22, serverType: 'HostBased' as 'HostBased' | 'UserBased',
        servicePort: 27020, licOptFilePath: '', vendorDaemonPort: 27021,
        snpslmdBinPath: '', mainLicFilePath: '',
    });

    // License File Update tab state
    const [licFileServer, setLicFileServer] = useState<string | null>(null);
    const [uploadedFileContent, setUploadedFileContent] = useState<string | null>(null);
    const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
    const [processedContent, setProcessedContent] = useState<string | null>(null);
    const [patchedLines, setPatchedLines] = useState<{ lineNumber: number; type: string; newLine: string }[]>([]);
    const [actualHostname, setActualHostname] = useState<string | null>(null);
    const [licFileState, setLicFileState] = useState<'idle' | 'ready' | 'processing' | 'preview' | 'applying' | 'result'>('idle');
    const [applyResult, setApplyResult] = useState<{ writeResult: any; rereadStdout: string; rereadStderr: string } | null>(null);
    const [applyConfirmVisible, setApplyConfirmVisible] = useState(false);

    // Function that returns default license column configuration
    const getDefaultLicenseColumns = () => [
        { key: 'id', label: 'ID', visible: true },
        { key: 'userId', label: 'User ID', visible: true },
        { key: 'email', label: 'Email', visible: true },
        { key: 'serverInfo', label: 'Server', visible: true },
        { key: 'toolName', label: 'Tool', visible: true },
        { key: 'licenseCount', label: '# Lics', visible: true },
        { key: 'licSrvrConnectStr', label: 'Connect Str', visible: false },
        { key: 'startDate', label: 'Start Date', visible: true },
        { key: 'endDate', label: 'End Date', visible: true },
        { key: 'status', label: 'License Status', visible: true },
        { key: 'source', label: 'Source', visible: false },
        { key: 'createdAt', label: 'Created At', visible: false },
        { key: 'updatedAt', label: 'Updated At', visible: false },
    ];

    // Column configuration state
    const [columnConfigOpen, setColumnConfigOpen] = useState(false);
    const [columns, setColumns] = useState(getDefaultLicenseColumns());

    // Function to show the modal
    const showModal = (type: string) => {
        console.log("Show model clicked for operation:", type)
        setOperationType(type);
        setIsModalVisible(true);
    };

    const operationTitles: { [key: string]: string } = {
        syncLicServer: 'Sync up with License Server',
        rereead: 'Re-read license entries on License Server',
        restart: 'Restart License Server',
        // Add more operations as needed
    };

    // Function to handle modal OK button
    const handleOk = async () => {
        console.log('OK button clicked');
        setIsModalVisible(false);
        if (operationType === 'rereead') {
            await executeRereadLicServer(remarks);
        } else if (operationType === 'syncLicServer') {
            await executeSyncLicServer(remarks);
        }
        setRemarks('');
    };

    // Function to handle modal Cancel button
    const handleCancel = () => {
        setIsModalVisible(false);
        setRemarks('');
    };

    // Update the button click handlers to show the modal
    const handleRereadLicServer = () => showModal('rereead');
    const handleSyncLicServer = () => showModal('syncLicServer');
    const handleRestartLicServer = () => showModal('restart');

  const executeRereadLicServer = async (remarks: string) => {
    console.log('Execute Reread License Server');
    if (!selectedServer) {
      eventBus.emit('show-toast', { summary: 'No server selected', message: 'Please select a license server first.', severity: 'error' });
      return;
    }
    const selectedServerObj = licenseServers.find(server => server.id.toString() === selectedServer);
    if (!selectedServerObj) {
      eventBus.emit('show-toast', { summary: 'Invalid server', message: 'Selected server not found.', severity: 'error' });
      return;
    }
    const server = selectedServerObj.licSrvrConnectStr; // e.g., serverFqdn:servicePort
    updateStatusMessageToOperationInProgress('Rereading License Server Operation in progress.... Wait...');
    try {
        const result = await rereadLicServer("GUI_Operator", { server, remarks });
        console.log('Reread result:', result);
        updateStatusMessageToInfo('Reread License Server completed! Check results');
        eventBus.emit('show-toast', { summary: 'Success', message: 'License server reread successfully.', severity: 'success' });
    } catch(error) {
        const err = error as Error;
        console.log("Failure in Rereading License Server",err);
        updateStatusMessageToError('Reread License Server operation FAILED! check results');
        eventBus.emit('show-toast', { summary: 'Failure', message: (error as any)?.response?.data?.message || err.message || 'Reread failed.', severity: 'error' });
    }
  };

  const executeSyncLicServer = async (remarks: string) => {
    console.log('Execute Sync Option File to License Server');
    if (!selectedServer) {
      eventBus.emit('show-toast', { summary: 'No server selected', message: 'Please select a license server first.', severity: 'error' });
      return;
    }
    const selectedServerObj = licenseServers.find(server => server.id.toString() === selectedServer);
    if (!selectedServerObj) {
      eventBus.emit('show-toast', { summary: 'Invalid server', message: 'Selected server not found.', severity: 'error' });
      return;
    }
    const server = selectedServerObj.licSrvrConnectStr; // e.g., serverFqdn:servicePort
    updateStatusMessageToOperationInProgress('Syncing License Options File Operation in progress.... Wait...');
    try {
        const result = await syncOptFileToLicServer("GUI_Operator", { server, remarks });
        console.log('Sync result:', result);
        updateStatusMessageToInfo('Sync License Options File completed! Check results');
        eventBus.emit('show-toast', { summary: 'Success', message: 'License options file synced successfully.', severity: 'success' });
    } catch(error) {
        const err = error as Error;
        console.log("Failure in Syncing License Options File",err);
        updateStatusMessageToError('Sync License Options File operation FAILED! check results');
        eventBus.emit('show-toast', { summary: 'Failure', message: (error as any)?.response?.data?.message || err.message || 'Sync failed.', severity: 'error' });
    }
  };

  // License Server Config handlers
  const openAddLicServerDialog = () => {
    setEditingServer(null);
    setLicServerForm({ serverFqdn: '', adminUserid: '', sshPort: 22, serverType: 'HostBased', servicePort: 27020, licOptFilePath: '', vendorDaemonPort: 27021, snpslmdBinPath: '', mainLicFilePath: '' });
    setLicServerDialogVisible(true);
  };

  const openEditLicServerDialog = (server: import('../../types').LicServer) => {
    setEditingServer(server);
    setLicServerForm({
      serverFqdn: server.serverFqdn,
      adminUserid: server.adminUserid,
      sshPort: server.sshPort,
      serverType: server.serverType,
      servicePort: server.servicePort,
      licOptFilePath: server.licOptFilePath,
      vendorDaemonPort: server.vendorDaemonPort ?? (server.servicePort + 1),
      snpslmdBinPath: server.snpslmdBinPath ?? '',
      mainLicFilePath: server.mainLicFilePath ?? '',
    });
    setLicServerDialogVisible(true);
  };

  const saveLicServer = async () => {
    setServerConfigLoading(true);
    try {
      if (editingServer) {
        const updated = await updateLicServer(editingServer.id, licServerForm);
        setLicenseServers(prev => prev.map(s => s.id === editingServer.id ? { ...s, ...updated } : s));
        eventBus.emit('show-toast', { summary: 'Success', message: 'License server updated.', severity: 'success' });
      } else {
        const created = await addLicServer(licServerForm);
        created.licSrvrConnectStr = `${created.serverFqdn}:${created.servicePort}`;
        setLicenseServers(prev => [...prev, created]);
        eventBus.emit('show-toast', { summary: 'Success', message: 'License server added.', severity: 'success' });
      }
      setLicServerDialogVisible(false);
    } catch (error) {
      const err = error as Error;
      eventBus.emit('show-toast', { summary: 'Error', message: (error as any)?.response?.data?.message || err.message || 'Failed to save license server.', severity: 'error' });
    } finally {
      setServerConfigLoading(false);
    }
  };

  const confirmDeleteLicServer = (server: import('../../types').LicServer) => {
    setServerToDelete(server);
    setDeleteServerDialogVisible(true);
  };

  const executeSoftDeleteLicServer = async () => {
    if (!serverToDelete) return;
    setServerConfigLoading(true);
    try {
      await deleteLicServer(serverToDelete.id);
      setLicenseServers(prev => prev.filter(s => s.id !== serverToDelete.id));
      eventBus.emit('show-toast', { summary: 'Success', message: 'License server deleted.', severity: 'success' });
      setDeleteServerDialogVisible(false);
      setServerToDelete(null);
    } catch (error) {
      const err = error as Error;
      eventBus.emit('show-toast', { summary: 'Error', message: (error as any)?.response?.data?.message || err.message || 'Failed to delete license server.', severity: 'error' });
    } finally {
      setServerConfigLoading(false);
    }
  };

  // License File Update handlers
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      setUploadedFileContent(content);
      setUploadedFileName(file.name);
      setProcessedContent(null);
      setPatchedLines([]);
      setActualHostname(null);
      setApplyResult(null);
      setLicFileState(prev => licFileServer ? 'ready' : 'idle');
    };
    reader.readAsText(file);
  };

  const handleProcessFile = async () => {
    if (!licFileServer || !uploadedFileContent) return;
    const selectedServerObj = licenseServers.find(s => s.id.toString() === licFileServer);
    if (!selectedServerObj) return;
    const connectStr = selectedServerObj.licSrvrConnectStr!;
    setLicFileState('processing');
    try {
      const result = await processLicenseFile(connectStr, uploadedFileContent);
      setProcessedContent(result.processedContent);
      setPatchedLines(result.patchedLines || []);
      setActualHostname(result.actualHostname || null);
      setLicFileState('preview');
    } catch (error) {
      const err = error as Error;
      setLicFileState('ready');
      eventBus.emit('show-toast', { summary: 'Error', message: (error as any)?.response?.data?.message || err.message || 'Failed to process file.', severity: 'error' });
    }
  };

  const handleApplyLicenseFile = async () => {
    if (!licFileServer || !processedContent) return;
    const selectedServerObj = licenseServers.find(s => s.id.toString() === licFileServer);
    if (!selectedServerObj) return;
    const connectStr = selectedServerObj.licSrvrConnectStr!;
    setApplyConfirmVisible(false);
    setLicFileState('applying');
    try {
      const result = await applyLicenseFile(connectStr, processedContent);
      setApplyResult(result);
      setLicFileState('result');
      eventBus.emit('show-toast', { summary: 'Success', message: 'License file applied and server reread triggered.', severity: 'success' });
    } catch (error) {
      const err = error as Error;
      setLicFileState('preview');
      const msg = (error as any)?.response?.data?.message || err.message || 'Failed to apply license file.';
      eventBus.emit('show-toast', { summary: 'Error', message: msg, severity: 'error' });
    }
  };

  const resetLicFileUpdate = () => {
    setUploadedFileContent(null);
    setUploadedFileName(null);
    setProcessedContent(null);
    setPatchedLines([]);
    setActualHostname(null);
    setApplyResult(null);
    setLicFileState('idle');
  };

  const updateStatusMessageToError = async (message:string) => {
            setStatusMessage(message);
            setStatusColor('red');
    };
  const updateStatusMessageToInfo = async (message:string) => {
            setStatusMessage(message);
            setStatusColor('blue');
    };
  const updateStatusMessageToOperationInProgress = async (message:string) => {
            setStatusMessage(message);
            setStatusColor('orange');
    };







    useEffect(() => {
        if (isAuthenticated && authValidated) {
            const fetchLicenseServers = async () => {
                try {
                    const servers = await getLicServers({});
                    if (!servers) {
                        console.error('No license servers found');
                        return;
                    }
                    // Generate connectStr for each server
                    servers.forEach((server) => {
                        server.licSrvrConnectStr = `${server.serverFqdn}:${server.servicePort}`;
                    });
                    // console.log('Fetched license servers:', servers);
                    setLicenseServers(servers);
                } catch (error) {
                    console.error('Error fetching license servers:', error);
                    eventBus.emit('show-toast', { message: 'Error fetching license servers', severity: 'error'});
                }
            };
            fetchLicenseServers();
        }
    }, [isAuthenticated, authValidated]);

    const [statusFilter, setStatusFilter] = useState<string | null>(null);
    const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
    const [selectedServer, setSelectedServer] = useState<string | null>(null);

    // Advanced filter state for license management table
    const [selectedFilterTools, setSelectedFilterTools] = useState<string[]>([]);
    const [selectedFilterStatuses, setSelectedFilterStatuses] = useState<string[]>([]);
    const [endDateFilter, setEndDateFilter] = useState<Date | null>(null);
    const [endDateOperator, setEndDateOperator] = useState<'gte' | 'lte'>('gte');

    // Clear all advanced filters
    const clearAllFilters = () => {
        setSelectedFilterTools([]);
        setSelectedFilterStatuses([]);
        setEndDateFilter(null);
        setEndDateOperator('gte');
    };

    // Check if any advanced filters are active
    const hasActiveFilters = selectedFilterTools.length > 0 ||
                            selectedFilterStatuses.length > 0 ||
                            endDateFilter !== null;

    // Get active filter count for badge
    const getActiveFilterCount = () => {
        return selectedFilterTools.length + selectedFilterStatuses.length + (endDateFilter ? 1 : 0);
    };

    const [selectedRows, setSelectedRows] = useState<number[]>([]);
    // const [selectedRowData, setSelectedRowData] = useState<LicenseTransactionEntity | null>(null);
    // const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
    // const [selectedRowId, setSelectedRowId] = useState<number | null>(null);
    // const [selectedRowUserId, setSelectedRowUserId] = useState<string | null>(null);
    // const [selectedRowServerInfo, setSelectedRowServerInfo] = useState<string | null>(null);
    // const [selectedRowToolName, setSelectedRowToolName] = useState<string | null>(null);
    // const [selectedRowLicenseCount, setSelectedRowLicenseCount] = useState<number | null>(null);
    // const [selectedRowStartDate, setSelectedRowStartDate] = useState<Date | null>(null);
    // const [selectedRowEndDate, setSelectedRowEndDate] = useState<Date | null>(null);
    // const [selectedRowStatus, setSelectedRowStatus] = useState<string | null>(null);
    // const [selectedRowSource, setSelectedRowSource] = useState<string | null>(null);

    const fetchLicenses = async (filter: Object) => {
            try {
                // const data = await getAllLicenses({ licSrvrConnectStr: selectedServer });
                const data = await getAllLicenses({ filter });
                if (!data) {
                    console.error('No licenses found');
                    setLicenses([]);
                    return;
                }
                console.log('Fetched licenses:', data);
                setLicenses(data);
            } catch (error) {
                console.error('Error fetching licenses:', error);
            }
    }

    // Load column preferences from localStorage
    useEffect(() => {
        const savedConfig = localStorage.getItem('licenseMgmtColumnConfig');
        if (savedConfig) {
            try {
                const parsedConfig = JSON.parse(savedConfig);
                setColumns(prevColumns =>
                    prevColumns.map(col => ({
                        ...col,
                        visible: parsedConfig[col.key] !== undefined ? parsedConfig[col.key] : col.visible
                    }))
                );
            } catch (error) {
                console.warn('Failed to load column configuration from localStorage:', error);
            }
        }
    }, []);

    // Save column preferences to localStorage
    const saveColumnConfig = (newColumns: typeof columns) => {
        const config = newColumns.reduce((acc, col) => {
            acc[col.key] = col.visible;
            return acc;
        }, {} as Record<string, boolean>);
        localStorage.setItem('licenseMgmtColumnConfig', JSON.stringify(config));
    };

    // Handle column visibility toggle
    const handleColumnToggle = (key: string) => {
        setColumns(prevColumns => {
            const newColumns = prevColumns.map(col =>
                col.key === key ? { ...col, visible: !col.visible } : col
            );
            saveColumnConfig(newColumns);
            return newColumns;
        });
    };

    // Reset columns to default visibility
    const resetColumnsToDefault = () => {
        const defaultColumns = getDefaultLicenseColumns();
        setColumns(defaultColumns);
        saveColumnConfig(defaultColumns);
    };

    useEffect(() => {
        if (isAuthenticated && authValidated) {
            fetchLicenses({});
            getUserIdWithMultipleEmails().then(setUserIdsWithMultipleEmails);
        }
    }, [isAuthenticated, authValidated]);

    const syncChangesWithLicenseServer = async () => {
        try {
            // Replace this with your API call to sync changes
            console.log('Syncing changes:' );
            // Example: await updateLicensesInDatabase(editedCells);
            eventBus.emit('show-toast', { summary: 'Feature not implemented yet', message: 'Sync changes with Lic Server is not implemented yet', severity: 'error'});

        } catch (error) {
            console.error('Error syncing changes:', error);
            eventBus.emit('show-toast', { summary: 'Sync changes with Lic Server', message: "" + error, severity: 'error'});
        }
    };

    const clearSelectedRows = () => {
        setLicenses((prevLicenses) =>
            prevLicenses.map((license) => ({
                ...license,
                selected: false,
            }))
        );
        setSelectedRows([]);
    };

    const deleteLicenseEntries = async (ids: number[]) => {
        try {
            // Replace this with your API call to delete licenses
            console.log('Deleting licenses with IDs:', ids);
            // Example: await deleteLicensesFromDatabase(ids);
            await deleteLicensesFromDatabase(ids);
            // Update the state to remove the deleted licenses

            setLicenses((prevLicenses) =>
                prevLicenses.filter((license) => !ids.includes(license.id))
            );
            eventBus.emit('show-toast', { summary: 'Licenses deleted successfully', message: `${ids.length} License entries deleted successfully!`, severity: 'success'});
        } catch (error) {
            console.error('Error deleting licenses:', error);
            eventBus.emit('show-toast', { summary: 'failure in Licenses deleted', message: '' + error, severity: 'error'});
        }
    };

    const handleUpdateLicenseStatus = (ids: number[], newStatus: string) => {
        setLicenses((prevLicenses) =>
            prevLicenses.map((license) =>
                ids.includes(license.id) ? { ...license, status: newStatus } : license
            )
        );
        // Write down the API call to update the status of the licenses
    };

    // Handle manual execution of license expiry status update job
    const handleUpdateLicExpiryStatus = async () => {
        try {
            console.log('Executing license expiry status update job...');

            // Use the configured API service instead of direct fetch
            const response = await api.post('/lic/jobs/updateLicExpiryStatus');

            eventBus.emit('show-toast', {
                summary: 'Job Executed Successfully',
                message: 'License expiry status update job completed. Check transaction logs for details.',
                severity: 'success'
            });

            // Refresh the license data to reflect any status changes
            fetchLicenses({});

        } catch (error) {
            console.error('Error executing license expiry status update job:', error);
            eventBus.emit('show-toast', {
                summary: 'Job Execution Failed',
                message: 'Failed to execute license expiry status update job. Please try again.',
                severity: 'error'
            });
        }
    };
 
    // Disable selected licenses
    const handleDisableLicenses = async () => {
        console.log('handleDisableLicenses called');
        const selectedRows = licenses.filter((license) => license.selected);
        console.log('Selected rows:', selectedRows);
        if (selectedRows.length === 0) {
            eventBus.emit('show-toast', { summary: 'No rows selected!', message: 'Please select at least one row to perform the operation.', severity: 'warn'});
            return;
        }

        // Show loading indicator
        setBulkOperationInProgress(true);
        setBulkOperationMessage(`Disabling ${selectedRows.length} license${selectedRows.length > 1 ? 's' : ''}...`);

        try {
            const licenseIds = selectedRows.map(row => row.id);
            console.log('Calling disableLicenses API with IDs:', licenseIds);
            const result = await disableLicenses(licenseIds);
            console.log('disableLicenses API result:', result);

            if (result.success !== false && result.disabledCount > 0) {
                // ✅ Update local state immediately for responsive UI
                setLicenses(prevLicenses =>
                    prevLicenses.map(license =>
                        licenseIds.includes(license.id)
                            ? { ...license, status: 'Disabled' }
                            : license
                    )
                );

                setBulkOperationMessage(`Successfully disabled ${result.disabledCount} licenses`);
                eventBus.emit('show-toast', {
                    summary: 'Licenses Disabled',
                    message: `${result.disabledCount} licenses disabled successfully`,
                    severity: 'success'
                });

                // 🔄 Refresh from database after 2 seconds to ensure consistency
                setTimeout(async () => {
                    try {
                        const data = await getAllLicenses({
                            licSrvrConnectStr: selectedServer ?
                                licenseServers.find(s => s.id.toString() === selectedServer)?.licSrvrConnectStr
                                : undefined
                        });
                        setLicenses(data || []);
                        setBulkOperationInProgress(false);
                        setBulkOperationMessage('');
                    } catch (refreshError) {
                        console.warn('Failed to refresh license data:', refreshError);
                        setBulkOperationInProgress(false);
                        setBulkOperationMessage('');
                    }
                }, 2000);

            } else {
                setBulkOperationMessage('Failed to disable licenses');
                eventBus.emit('show-toast', {
                    summary: 'Operation Failed',
                    message: result.message || 'Failed to disable licenses',
                    severity: 'error'
                });
                setTimeout(() => {
                    setBulkOperationInProgress(false);
                    setBulkOperationMessage('');
                }, 3000);
            }
        } catch (error) {
            console.error('Error disabling licenses:', error);
            setBulkOperationMessage('Error occurred while disabling licenses');
            eventBus.emit('show-toast', {
                summary: 'Error',
                message: 'Failed to disable licenses',
                severity: 'error'
            });
            setTimeout(() => {
                setBulkOperationInProgress(false);
                setBulkOperationMessage('');
            }, 3000);
        }
    };

    // Enable selected licenses
    const handleEnableLicenses = async () => {
        const selectedRows = licenses.filter((license) => license.selected);
        if (selectedRows.length === 0) {
            eventBus.emit('show-toast', { summary: 'No rows selected!', message: 'Please select at least one row to perform the operation.', severity: 'warn'});
            return;
        }

        // ✅ Show loading indicator
        setBulkOperationInProgress(true);
        setBulkOperationMessage(`Enabling ${selectedRows.length} license${selectedRows.length > 1 ? 's' : ''}...`);

        try {
            const licenseIds = selectedRows.map(row => row.id);
            const result = await enableLicenses(licenseIds);

            if (result.success !== false && result.enabledCount > 0) {
                // ✅ Update local state immediately for responsive UI
                setLicenses(prevLicenses =>
                    prevLicenses.map(license =>
                        licenseIds.includes(license.id)
                            ? { ...license, status: 'Active' }
                            : license
                    )
                );

                setBulkOperationMessage(`Successfully enabled ${result.enabledCount} licenses`);
                eventBus.emit('show-toast', {
                    summary: 'Licenses Enabled',
                    message: `${result.enabledCount} licenses enabled successfully`,
                    severity: 'success'
                });

                // 🔄 Refresh from database after 2 seconds to ensure consistency
                setTimeout(async () => {
                    try {
                        const data = await getAllLicenses({
                            licSrvrConnectStr: selectedServer ?
                                licenseServers.find(s => s.id.toString() === selectedServer)?.licSrvrConnectStr
                                : undefined
                        });
                        setLicenses(data || []);
                        setBulkOperationInProgress(false);
                        setBulkOperationMessage('');
                    } catch (refreshError) {
                        console.warn('Failed to refresh license data:', refreshError);
                        setBulkOperationInProgress(false);
                        setBulkOperationMessage('');
                    }
                }, 2000);

            } else {
                setBulkOperationMessage('Failed to enable licenses');
                eventBus.emit('show-toast', {
                    summary: 'Operation Failed',
                    message: result.message || 'Failed to enable licenses',
                    severity: 'error'
                });
                setTimeout(() => {
                    setBulkOperationInProgress(false);
                    setBulkOperationMessage('');
                }, 3000);
            }
        } catch (error) {
            console.error('Error enabling licenses:', error);
            setBulkOperationMessage('Error occurred while enabling licenses');
            eventBus.emit('show-toast', {
                summary: 'Error',
                message: 'Failed to enable licenses',
                severity: 'error'
            });
            setTimeout(() => {
                setBulkOperationInProgress(false);
                setBulkOperationMessage('');
            }, 3000);
        }
    };

    // Show extension request dialog
    const showExtensionDialog = () => {
        const selectedRows = licenses.filter((license) => license.selected);
        if (selectedRows.length === 0) {
            eventBus.emit('show-toast', { summary: 'No rows selected!', message: 'Please select at least one row to perform the operation.', severity: 'warn'});
            return;
        }
        console.log('=== FRONTEND EXTENSION DIALOG OPENED ===');
        console.log('Selected licenses for extension:', selectedRows.map(row => ({ id: row.id, toolName: row.toolName, userId: row.userId })));
        setExtensionDialogVisible(true);
    };

    // Handle extension request submission
    const handleExtensionRequest = async () => {
        console.log('=== FRONTEND EXTENSION REQUEST SUBMISSION ===');

        if (!extensionDate) {
            console.log('Validation failed: No extension date selected');
            eventBus.emit('show-toast', { summary: 'No date selected', message: 'Please select an extension date.', severity: 'warn'});
            return;
        }

        const selectedRows = licenses.filter((license) => license.selected);
        const licenseIds = selectedRows.map(row => row.id);

        console.log('Selected license IDs:', licenseIds);
        console.log('Extension date:', extensionDate.toISOString());
        console.log('Remarks:', extensionRemarks);

        const requestData = {
            licenseIds: licenseIds,
            extensionDate: extensionDate.toISOString(),
            source: "Gui-LicenseManagementPage",
            remarks: extensionRemarks
        };

        console.log('Sending request data to API:', requestData);

        try {
            console.log('Calling requestLicenseExtension API...');
            const result = await requestLicenseExtension(requestData);

            console.log('API Response received:', result);

            if (result.successCount > 0) {
                console.log(`Extension request successful: ${result.successCount} licenses`);
                eventBus.emit('show-toast', {
                    summary: 'Extension Requested',
                    message: `Extension request submitted for ${result.successCount} licenses until ${extensionDate.toLocaleDateString()}`,
                    severity: 'success'
                });

                // Clear selections after successful request
                setLicenses(prevLicenses =>
                    prevLicenses.map(license => ({ ...license, selected: false }))
                );
            }

            if (result.errorCount > 0) {
                console.log(`Extension request partial failure: ${result.errorCount} errors`);
                eventBus.emit('show-toast', {
                    summary: 'Partial Success',
                    message: `${result.errorCount} extension requests failed. Check logs for details.`,
                    severity: 'warn'
                });
            }

        } catch (error) {
            console.error('=== FRONTEND EXTENSION REQUEST ERROR ===');
            console.error('Error details:', error);
            eventBus.emit('show-toast', {
                summary: 'Extension Request Failed',
                message: 'Failed to submit extension request. Please try again.',
                severity: 'error'
            });
        }

        // Reset dialog state
        console.log('Resetting extension dialog state');
        setExtensionDialogVisible(false);
        setExtensionDate(null);
        setExtensionRemarks('');
    };

    const handleSelectedRowsOperation = (operation:string) => {
        const selectedRows = licenses.filter((license) => license.selected);
        console.log(`Performing ${operation} operation Selected Rows:`, selectedRows);
        // Perform your operation on the selected rows here
        // For example, you can update the status of selected rows
        switch (operation) {
            case 'Remove':
                if (selectedRows.length === 0) {
                    eventBus.emit('show-toast', { summary: 'No rows selected!', message: 'Please select at least one row to perform the operation.', severity: 'warn'});
                    return;
                }
                        deleteLicenseEntries(selectedRows.map((row) => row.id));
                break;
            case 'Disable':
                handleDisableLicenses();
                break;
            case 'Enable':
                handleEnableLicenses();
                break;
            case 'Extend':
                showExtensionDialog();
                break;
            case 'Add':
            default:
                eventBus.emit('show-toast', { summary: 'Operation Not available!', message: `Operation ${operation} has not been implemented yet .`, severity: 'warn'});
                break;
        }
        // selectedRows.forEach((row) => {
        //     // Perform your operation here
        //     console.log(`Performing operation ${operation} on row with ID: ${row.id}`);
        // });
        // alert(`Operation ${operation} performed on ${selectedRows.length} selected rows!`);
    };

    // License History Modal functions
    const openLicenseHistoryModal = async (licenseId: number) => {
        try {
            setModalLoading(true);
            // Fetch license history from API
            const data = await getLicenseHistory(licenseId);
            setSelectedLicenseSummary(data.summary);
            setSelectedLicenseHistory(data.history);
            setShowLicenseHistory(true);
        } catch (error) {
            console.error('Error fetching license history:', error);
            eventBus.emit('show-toast', {
                summary: 'Error',
                message: 'Failed to load license history',
                severity: 'error'
            });
        } finally {
            setModalLoading(false);
        }
    };

    const selectedLicenseCount = licenses.filter((l) => l.selected).length;
    const hasSelectedLicenses = selectedLicenseCount > 0;

    return (
        <div
            className={`flex-1 transition-all duration-300 ${
                useNew ? 'bg-surface-app font-plex' : 'bg-white'
            } ${isSidebarOpen ? 'ml-64' : 'ml-20'}`}
            style={useNew ? undefined : { fontFamily: "'Roboto', sans-serif" }}
        >
        {/* Page Title - Compact Layout */}
        <div className="mb-2 px-6 pt-6">
            {useNew ? (
                <PageHeader title="License Administration" purpose={t.manageLicensesServerOps} />
            ) : (
                <div className="flex items-center justify-between">
                    <h1 className="text-2xl font-bold text-gray-800">{t.licenseAdministrationPageTitle}</h1>
                    <span className="text-sm text-gray-500">{t.manageLicensesServerOps}</span>
                </div>
            )}
        </div>

        {/* Tab Navigation */}
        <div className={`flex border-b mx-6 mt-2 mb-0 ${useNew ? 'border-border-strong' : 'border-gray-200'}`}>
            {(['records', 'serverConfig', 'licUpdate'] as const).map((tab) => {
                const labels: Record<typeof tab, string> = {
                    records: 'License Records',
                    serverConfig: 'License Server Config',
                    licUpdate: 'License File Update',
                };
                const isActive = activeTab === tab;
                return (
                    <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`px-5 py-2 text-sm font-medium border-b-2 transition-colors ${
                            isActive
                                ? useNew
                                    ? 'border-accent text-accent bg-accent/5'
                                    : 'border-blue-600 text-blue-700 bg-blue-50'
                                : useNew
                                    ? 'border-transparent text-ink-secondary hover:text-ink-primary hover:border-border-subtle'
                                    : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'
                        }`}
                    >
                        {labels[tab]}
                    </button>
                );
            })}
        </div>

        {/* ── Tab: License Records ── */}
        {activeTab === 'records' && <>

        {/* Ultra-Compact Toolbar - token chrome when useNew, slate gradient for classic */}
        <div className={useNew
            ? 'bg-surface-raised border-2 border-border-strong rounded mx-6 mt-6 mb-4 p-4'
            : 'bg-gradient-to-r from-slate-50 via-gray-50 to-zinc-100 border border-gray-200 rounded-lg mx-6 mt-6 mb-4 shadow-sm p-4'
        }>
          {/* Main Toolbar Row - Always Visible */}
          <div className="flex items-center justify-between flex-wrap gap-3">

            {/* Left Section: License Server Operations */}
            <div className="flex items-center gap-3 flex-wrap">
              {/* Server Selection */}
              <div className="flex items-center gap-2">
                <span className={useNew ? 'text-sm font-medium text-ink-secondary' : 'text-sm font-medium text-gray-700'}>{t.licenseServerLabel}</span>
                <Dropdown
                  value={selectedServer}
                  options={licenseServers.map((server) => ({
                    label: `${server.licSrvrConnectStr}`,
                    value: `${server.id}`,
                  }))}
                  onChange={(e) => {
                    console.log('Server Dropdown value changed to:', e);
                    console.log('Selected Server:', e.value);
                    setSelectedServer(e.value);
                    let selectedOption = licenseServers.find((server) => server.id.toString() === e.value);
                    if (selectedOption) {
                      console.log(`Selected Option:`, selectedOption);
                      fetchLicenses({ licSrvrConnectStr: selectedOption.licSrvrConnectStr });
                      console.log(`Licenses fetched for server: ${selectedOption.licSrvrConnectStr}`);
                    }
                  }}
                  placeholder="Select License Server"
                  style={{ width: '280px' }}
                />
              </div>

              {/* Server Operations */}
              <div className="flex items-center gap-3">
                <Button
                  label={t.syncOptions}
                  icon="pi pi-sync"
                  className={`px-4 py-2 font-medium rounded-lg shadow-md transition-all duration-200 border-0 ${
                    useNew
                      ? selectedServer
                        ? 'bg-success text-white hover:opacity-90'
                        : 'bg-surface-raised text-ink-secondary opacity-60 cursor-not-allowed'
                      : selectedServer
                        ? 'bg-gradient-to-r from-green-600 to-green-700 text-white hover:from-green-700 hover:to-green-800 hover:shadow-lg'
                        : 'bg-gray-400 text-gray-200 cursor-not-allowed opacity-60'
                  }`}
                  onClick={handleSyncLicServer}
                  disabled={!selectedServer}
                  title={selectedServer ? "Sync license server options file" : "Select a license server first"}
                />

                {/* Option File Path Display - Shows which file is being affected by sync/reread operations */}
                {selectedServer && (
                  <div className={useNew
                    ? 'flex items-center gap-2 px-3 py-2 bg-surface border-2 border-border-strong rounded-lg'
                    : 'flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg'
                  }>
                    <span className={useNew ? 'text-xs font-medium text-ink-secondary' : 'text-xs font-medium text-gray-600'}>{t.optionFileLabel}</span>
                    <span className={useNew ? 'text-xs font-mono text-ink-primary max-w-96 truncate' : 'text-xs font-mono text-gray-800 max-w-96 truncate'} title={licenseServers.find(s => s.id.toString() === selectedServer)?.licOptFilePath}>
                      {licenseServers.find(s => s.id.toString() === selectedServer)?.licOptFilePath || 'N/A'}
                    </span>
                  </div>
                )}

                <Button
                  label={t.rereadServer}
                  icon="pi pi-server"
                  className={`px-4 py-2 font-medium rounded-lg shadow-md transition-all duration-200 border-0 ${
                    useNew
                      ? selectedServer
                        ? 'bg-accent text-white hover:bg-accent-hover'
                        : 'bg-surface-raised text-ink-secondary opacity-60 cursor-not-allowed'
                      : selectedServer
                        ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 hover:shadow-lg'
                        : 'bg-gray-400 text-gray-200 cursor-not-allowed opacity-60'
                  }`}
                  onClick={handleRereadLicServer}
                  disabled={!selectedServer}
                  title={selectedServer ? "Reread license server configuration" : "Select a license server first"}
                />
              </div>
            </div>

            {/* Middle Section: License Record Operations */}
            <div className="flex gap-2 flex-wrap">
              <Button
                label={t.disableSelected}
                icon="pi pi-ban"
                className={useNew
                  ? `px-4 py-2 font-medium rounded-lg shadow-md transition-all duration-200 border-0 ${
                      hasSelectedLicenses
                        ? 'bg-warning text-white hover:opacity-90'
                        : 'bg-surface-raised text-ink-secondary opacity-60 cursor-not-allowed'
                    }`
                  : 'px-4 py-2 bg-gradient-to-r from-yellow-600 to-orange-600 text-white font-medium rounded-lg shadow-md hover:from-yellow-700 hover:to-orange-700 hover:shadow-lg transition-all duration-200 border-0'
                }
                onClick={() => handleSelectedRowsOperation('Disable')}
                disabled={!hasSelectedLicenses}
                title="Disable selected licenses"
              />

              <Button
                label={t.enableSelected}
                icon="pi pi-check-circle"
                className={useNew
                  ? `px-4 py-2 font-medium rounded-lg shadow-md transition-all duration-200 border-0 ${
                      hasSelectedLicenses
                        ? 'bg-success text-white hover:opacity-90'
                        : 'bg-surface-raised text-ink-secondary opacity-60 cursor-not-allowed'
                    }`
                  : 'px-4 py-2 bg-gradient-to-r from-green-600 to-green-700 text-white font-medium rounded-lg shadow-md hover:from-green-700 hover:to-green-800 hover:shadow-lg transition-all duration-200 border-0'
                }
                onClick={() => handleSelectedRowsOperation('Enable')}
                disabled={!hasSelectedLicenses}
                title="Enable selected licenses"
              />

              <Button
                label={t.requestExtension}
                icon="pi pi-calendar-plus"
                className={useNew
                  ? `px-4 py-2 font-medium rounded-lg shadow-md transition-all duration-200 border-0 ${
                      hasSelectedLicenses
                        ? 'bg-accent text-white hover:bg-accent-hover'
                        : 'bg-surface-raised text-ink-secondary opacity-60 cursor-not-allowed'
                    }`
                  : 'px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-medium rounded-lg shadow-md hover:from-blue-700 hover:to-blue-800 hover:shadow-lg transition-all duration-200 border-0'
                }
                onClick={() => handleSelectedRowsOperation('Extend')}
                disabled={!hasSelectedLicenses}
                title="Request license extension"
              />

              <Button
                label={t.removeSelected}
                icon="pi pi-trash"
                className={useNew
                  ? `px-4 py-2 font-medium rounded-lg shadow-md transition-all duration-200 border-0 ${
                      hasSelectedLicenses
                        ? 'bg-danger text-white hover:opacity-90'
                        : 'bg-surface-raised text-ink-secondary opacity-60 cursor-not-allowed'
                    }`
                  : 'px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 text-white font-medium rounded-lg shadow-md hover:from-red-700 hover:to-red-800 hover:shadow-lg transition-all duration-200 border-0'
                }
                onClick={() => handleSelectedRowsOperation('Remove')}
                disabled={!hasSelectedLicenses}
                title="Remove selected licenses"
              />

              <Button
                label={t.updateExpiryStatus}
                icon="pi pi-clock"
                className={useNew
                  ? 'px-4 py-2 bg-info text-white font-medium rounded-lg shadow-md hover:opacity-90 transition-all duration-200 border-0'
                  : 'px-4 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white font-medium rounded-lg shadow-md hover:from-purple-700 hover:to-purple-800 hover:shadow-lg transition-all duration-200 border-0'
                }
                onClick={handleUpdateLicExpiryStatus}
                title="Manually update license expiry status (normally runs daily at 4am)"
              />
            </div>

            {/* Right Section: Tools & Filters */}
            <div className="flex items-center gap-3">
              <Button
                label={t.configureColumns}
                icon="pi pi-cog"
                className={useNew
                  ? 'px-4 py-2 bg-surface text-accent border border-accent font-medium rounded-lg shadow-sm hover:bg-accent/5 transition-all duration-200'
                  : 'px-4 py-2 bg-gradient-to-r from-gray-600 to-gray-700 text-white font-medium rounded-lg shadow-md hover:from-gray-700 hover:to-gray-800 hover:shadow-lg transition-all duration-200 border-0'
                }
                onClick={() => setColumnConfigOpen(true)}
                title="Configure which columns to display"
              />

              {/* Integrated Filters */}
              <div className={`flex items-center gap-2 pl-4 border-l ${useNew ? 'border-border-subtle' : 'border-gray-300'}`}>
                {!useNew && <span className="text-sm font-medium text-gray-700">🔍</span>}

                <MultiSelect
                  value={selectedFilterTools}
                  options={Array.from(new Set(licenses.map(license => license.toolName))).map(tool => ({
                    label: tool,
                    value: tool
                  }))}
                  onChange={(e: any) => setSelectedFilterTools(e.value)}
                  placeholder="Tools"
                  showClear
                  className="w-28 text-xs"
                  panelClassName="text-xs"
                />

                <MultiSelect
                  value={selectedFilterStatuses}
                  options={[
                    { label: 'Active', value: 'Active' },
                    { label: 'Disabled', value: 'Disabled' },
                    { label: 'Expired', value: 'Expired' }
                  ]}
                  onChange={(e) => setSelectedFilterStatuses(e.value)}
                  placeholder="Status"
                  showClear
                  className="w-28 text-xs"
                />

                <div className="flex items-center gap-1">
                  <Dropdown
                    value={endDateOperator}
                    options={[
                      { label: '≥', value: 'gte' },
                      { label: '≤', value: 'lte' }
                    ]}
                    onChange={(e) => setEndDateOperator(e.value)}
                    className="w-10 text-xs"
                  />
                  <Calendar
                    value={endDateFilter}
                    onChange={(e) => e.value && setEndDateFilter(e.value)}
                    placeholder="Date"
                    showIcon
                    className="w-24 text-xs"
                  />
                </div>

                {/* Enhanced Clear Button */}
                <Button
                  label="Clear"
                  icon="pi pi-times-circle"
                  className={useNew
                    ? 'px-3 py-1 bg-danger/10 hover:bg-danger/20 text-danger border border-danger/30 rounded-md text-xs font-medium transition-colors shadow-sm'
                    : 'px-3 py-1 bg-red-100 hover:bg-red-200 text-red-700 border border-red-300 rounded-md text-xs font-medium transition-colors shadow-sm'
                  }
                  onClick={clearAllFilters}
                  disabled={!hasActiveFilters}
                  title="Clear all filters"
                />

                {/* Filter Count Badge */}
                {hasActiveFilters && (
                  <Badge
                    value={getActiveFilterCount()}
                    severity="info"
                    className="text-xs ml-1"
                  />
                )}
              </div>
            </div>
          </div>

          {/* Dynamic Status Row - Only shows when there's information to display */}
          {(statusMessage || hasSelectedLicenses) && (
            <div className={`flex items-center justify-between mt-3 pt-3 border-t ${useNew ? 'border-border-subtle' : 'border-purple-200'}`}>
              <div className="flex items-center gap-3">
                {hasSelectedLicenses && (
                  <div className={useNew
                    ? 'bg-accent/10 border border-accent/30 rounded-lg px-3 py-2 text-sm font-medium text-accent'
                    : 'bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-sm font-medium text-blue-800'
                  }>
                    {useNew ? '' : '✓ '}{selectedLicenseCount} license{selectedLicenseCount > 1 ? 's' : ''} selected
                  </div>
                )}
              </div>

              <div className="flex items-center">
                {statusMessage && (
                  <div className={`px-3 py-2 rounded-full text-sm font-medium shadow-sm ${
                    statusColor === 'blue'
                      ? useNew
                        ? 'bg-info/10 text-info border border-info/30'
                        : 'bg-blue-100 text-blue-800 border border-blue-200'
                      : statusColor === 'red'
                        ? useNew
                          ? 'bg-danger/10 text-danger border border-danger/30'
                          : 'bg-red-100 text-red-800 border border-red-200'
                        : useNew
                          ? 'bg-warning/10 text-warning border border-warning/30'
                          : 'bg-orange-100 text-orange-800 border border-orange-200'
                  }`}>
                    {statusMessage}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Modal for remarks */}
        <Modal
          title={operationTitles[operationType || ''] || 'Unknown Operation'}
          open={isModalVisible}
          onOk={handleOk}
          onCancel={handleCancel}
        >
          <Input.TextArea
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Enter remarks"
            rows={4}
          />
        </Modal>

            <div style={{ marginTop: '20px' }} className={useNew ? 'mx-6 border-2 border-border-strong rounded overflow-hidden' : undefined}>
            <DataTable
                value={licenses.filter(license => {
                    // Tool filter
                    const toolMatch = selectedFilterTools.length === 0 ||
                                     selectedFilterTools.includes(license.toolName);

                    // Status filter
                    const statusMatch = selectedFilterStatuses.length === 0 ||
                                       selectedFilterStatuses.includes(license.status);

                    // End date filter
                    const endDateMatch = !endDateFilter ||
                                       (license.plannedEndDate &&
                                        (endDateOperator === 'gte' ?
                                         new Date(license.plannedEndDate) >= endDateFilter :
                                         new Date(license.plannedEndDate) <= endDateFilter));

                    return toolMatch && statusMatch && endDateMatch;
                })}
                paginator
                rows={20}
                dataKey="id"
                tableStyle={{ minWidth: '50rem' }}
                className={useNew ? 'p-datatable-striped new-gui-datatable' : 'p-datatable-striped'}
                editMode="cell"
                globalFilterFields={globalFilterFields}
                selectionMode="checkbox"
                rowClassName={(data, index) => `row-${index}`}
                selection={licenses.filter((license) => license.selected)}
                onSelectionChange={(e) => {
                    const selectedIds = e.value.map((row: any) => row.id);
                    setLicenses((prevLicenses) =>
                        prevLicenses.map((license) => ({
                            ...license,
                            selected: selectedIds.includes(license.id),
                        }))
                    );
                    // console.log(selectedIds);
                }}
                onRowClick={(e) => {
                    // console.log(`Row has been clicked:`, e)
                    const clickedRowId = e.data.id;
                    setLicenses((prevLicenses) =>
                        prevLicenses.map((license) =>
                            license.id === clickedRowId
                                ? { ...license, selected: !license.selected }
                                : license
                        )
                    );
                }}
            >
                <Column
                    selectionMode="multiple"
                    headerStyle={{ width: '2em', textAlign: 'center', backgroundColor: useNew ? '#F8F9FB' : '#e4e4e4' }}
                    bodyStyle={{ textAlign: 'center', backgroundColor: useNew ? '#F8F9FB' : '#d0e0d0' }}
                />
                {columns.filter(col => col.visible).map((column) => {
                    if (column.key === 'endDate') {
                        return (
                            <Column
                                key={column.key}
                                field={column.key}
                                header={column.label}
                                sortable
                                sortFunction={(e) => {
                                    // Custom sort function for dates
                                    // Sort by actual date values, not display strings
                                    const { data, order } = e;
                                    return [...data].sort((a, b) => {
                                        const dateA = a.status === 'Expired' ? a.endDate : a.plannedEndDate;
                                        const dateB = b.status === 'Expired' ? b.endDate : b.plannedEndDate;

                                        const dateAValue = dateA ? new Date(dateA).getTime() : 0;
                                        const dateBValue = dateB ? new Date(dateB).getTime() : 0;

                                        if (order === 1) { // Ascending
                                            return dateAValue - dateBValue;
                                        } else { // Descending
                                            return dateBValue - dateAValue;
                                        }
                                    });
                                }}
                                filter
                                filterPlaceholder={`Search by ${column.label}`}
                                body={(rowData: LicenseTransactionEntity) => {
                                    // Show plannedEndDate for active licenses, endDate for expired licenses
                                    const displayDate = rowData.status === 'Expired'
                                        ? rowData.endDate
                                        : rowData.plannedEndDate;
                                    return displayDate ? new Date(displayDate).toLocaleDateString() : 'No expiry set';
                                }}
                            />
                        );
                    } else if (column.key === 'status') {
                        return (
                            <Column
                                key={column.key}
                                field={column.key}
                                header={column.label}
                                sortable
                                filter
                                filterPlaceholder={`Search by ${column.label}`}
                                body={(rowData: LicenseTransactionEntity) => (
                                    <LicenseStatus
                                        license={rowData}
                                        showIcon={true}
                                        size="small"
                                        showExpiryWarning={true}
                                    />
                                )}
                                style={{ minWidth: '150px' }}
                            />
                        );
                    } else if (column.key === 'id') {
                        return (
                            <Column
                                key={column.key}
                                field={column.key}
                                header={column.label}
                                sortable
                                filter
                                filterPlaceholder={`Search by ${column.label}`}
                                body={(rowData) => (
                                    <button
                                        className={useNew ? 'text-accent hover:text-accent-hover underline' : 'text-blue-600 hover:text-blue-800 underline'}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            openLicenseHistoryModal(rowData.id);
                                        }}
                                    >
                                        {rowData.id}
                                    </button>
                                )}
                                style={{ width: '80px' }}
                            />
                        );
                    } else if (column.key === 'email') {
                        return (
                            <Column
                                key={column.key}
                                field="uniqueUser.email"
                                header={column.label}
                                sortable
                                filter
                                filterPlaceholder={`Search by ${column.label}`}
                                body={(rowData: LicenseTransactionEntity) => {
                                    const email = rowData.uniqueUser?.email ?? '';
                                    if (!email) return <span className="text-gray-400">—</span>;
                                    return (
                                        <button
                                            type="button"
                                            className={useNew ? 'text-accent hover:text-accent-hover underline text-left' : 'text-blue-600 hover:text-blue-800 underline text-left'}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                navigate(`/user/${encodeURIComponent(email)}`);
                                            }}
                                        >
                                            {email}
                                        </button>
                                    );
                                }}
                                style={{ minWidth: '140px' }}
                            />
                        );
                    } else if (column.key === 'userId') {
                        return (
                            <Column
                                key={column.key}
                                field={column.key}
                                header={column.label}
                                sortable
                                filter
                                filterPlaceholder={`Search by ${column.label}`}
                                body={(rowData: LicenseTransactionEntity) => {
                                    const uid = rowData.userId ?? '';
                                    const isMultiple = uid ? userIdsWithMultipleEmails.includes(uid) : false;
                                    return (
                                        <span className={isMultiple ? (useNew ? 'text-warning font-medium' : 'text-amber-600 font-medium') : ''} title={isMultiple ? 'This user-id is linked to more than one email (unique user).' : undefined}>
                                            {uid || '—'}
                                            {isMultiple && <i className={useNew ? 'pi pi-exclamation-triangle text-warning ml-1' : 'pi pi-exclamation-triangle text-amber-600 ml-1'} title="Linked to multiple emails" aria-hidden />}
                                        </span>
                                    );
                                }}
                                style={{ minWidth: '100px' }}
                            />
                        );
                    } else {
                        return (
                            <Column
                                key={column.key}
                                field={column.key}
                                header={column.label}
                                sortable
                                filter
                                filterPlaceholder={`Search by ${column.label}`}
                            />
                        );
                    }
                })}
            </DataTable>

            </div>

            {/* Extension Request Dialog */}
            <Dialog
                header={t.requestLicenseExtension}
                visible={extensionDialogVisible}
                style={{ width: '450px' }}
                footer={
                    <div>
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            onClick={() => {
                                setExtensionDialogVisible(false);
                                setExtensionDate(null);
                                setExtensionRemarks('');
                            }}
                            className="p-button-text"
                        />
                        <Button
                            label="Request Extension"
                            icon="pi pi-calendar-plus"
                            onClick={handleExtensionRequest}
                            className="p-button-primary"
                            disabled={!extensionDate}
                        />
                    </div>
                }
                onHide={() => {
                    setExtensionDialogVisible(false);
                    setExtensionDate(null);
                    setExtensionRemarks('');
                }}
            >
                <div className="p-fluid">
                    <div className="p-field mb-4">
                        <label htmlFor="extensionDate" className="block text-sm font-medium text-gray-700 mb-2">
                            Extension Date *
                        </label>
                        <Calendar
                            id="extensionDate"
                            value={extensionDate}
                            onChange={(e) => setExtensionDate(e.value as Date)}
                            minDate={new Date()}
                            showIcon
                            placeholder="Select extension date"
                            className="w-full"
                        />
                        <small className="text-gray-500 mt-1 block">
                            Select the date until which you want the licenses extended
                        </small>
                    </div>

                    <div className="p-field">
                        <label htmlFor="extensionRemarks" className="block text-sm font-medium text-gray-700 mb-2">
                            Remarks (Optional)
                        </label>
                        <InputTextarea
                            id="extensionRemarks"
                            value={extensionRemarks}
                            onChange={(e) => setExtensionRemarks(e.target.value)}
                            rows={3}
                            placeholder="Enter any additional remarks or justification for the extension request..."
                            className="w-full"
                        />
                    </div>
                </div>
            </Dialog>

            {/* Column Configuration Dialog */}
            <Dialog
                header={t.configureVisibleColumns}
                visible={columnConfigOpen}
                style={{ width: '450px' }}
                footer={
                    <div>
                        <Button
                            label="Reset to Default"
                            icon="pi pi-refresh"
                            className="p-button-secondary"
                            onClick={resetColumnsToDefault}
                        />
                        <Button
                            label="Close"
                            icon="pi pi-times"
                            onClick={() => setColumnConfigOpen(false)}
                        />
                    </div>
                }
                onHide={() => setColumnConfigOpen(false)}
            >
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <p style={{ marginBottom: '1rem', color: '#666' }}>
                        Select which columns to display in the table:
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem' }}>
                        {columns.map((column) => (
                            <label key={column.key} style={{ display: 'flex', alignItems: 'center', padding: '0.25rem' }}>
                                <Checkbox
                                    inputId={column.key}
                                    checked={column.visible}
                                    onChange={() => handleColumnToggle(column.key)}
                                    style={{ marginRight: '0.5rem' }}
                                />
                                {column.label}
                            </label>
                        ))}
                    </div>
                </div>
            </Dialog>

            {/* License History Modal */}
            <Dialog
                header="License History"
                visible={showLicenseHistory}
                onHide={() => setShowLicenseHistory(false)}
                style={{ width: '900px', maxWidth: '90vw' }}
                maximizable
                className="p-fluid"
            >
                {selectedLicenseSummary && (
                    <div className="mb-6">
                        <h3 className="text-lg font-semibold mb-4">License Summary</h3>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
                            <div><strong>License ID:</strong> {selectedLicenseSummary.licenseId}</div>
                            <div><strong>User:</strong> {selectedLicenseSummary.infraUser}</div>
                            <div><strong>Tool:</strong> {selectedLicenseSummary.toolName}</div>
                            <div><strong>License Count:</strong> {selectedLicenseSummary.numLicenses}</div>
                            <div><strong>License Server:</strong> {selectedLicenseSummary.licSrvrConnectStr}</div>
                            <div><strong>Start Date:</strong> {new Date(selectedLicenseSummary.initialStartDate).toLocaleDateString()}</div>
                            <div><strong>Current End Date:</strong> {new Date(selectedLicenseSummary.currentPlannedEndDate).toLocaleDateString()}</div>
                            <div><strong>Extensions:</strong> {selectedLicenseSummary.numExtensions}</div>
                            <div><strong>Total Extension Days:</strong> {selectedLicenseSummary.totalExtensionDays}</div>
                            <div><strong>Last Updated:</strong> {new Date(selectedLicenseSummary.lastUpdated).toLocaleString()}</div>
                        </div>
                    </div>
                )}

                <div>
                    <h3 className="text-lg font-semibold mb-4">State History Timeline</h3>
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                        {selectedLicenseHistory.map((entry: any, index: number) => (
                            <div key={entry.id} className="border rounded-lg p-4 bg-white">
                                <div className="flex justify-between items-start mb-2">
                                    <div className="flex items-center gap-2">
                                        <span className="font-medium">#{entry.id}</span>
                                        <span className="text-sm text-gray-500">
                                            {new Date(entry.changed_at).toLocaleString()}
                                        </span>
                                    </div>
                                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                                        entry.old_state === entry.new_state
                                            ? 'bg-blue-100 text-blue-800'
                                            : 'bg-green-100 text-green-800'
                                    }`}>
                                        {entry.old_state || 'Created'} → {entry.new_state}
                                    </span>
                                </div>

                                {entry.change_reason && (
                                    <div className="text-sm text-gray-700 mb-2">
                                        <strong>Details:</strong> {entry.change_reason}
                                    </div>
                                )}

                                <div className="text-xs text-gray-500">
                                    <strong>Operator:</strong> {entry.operator || 'System'}
                                    {entry.start_date && (
                                        <> | <strong>Start:</strong> {new Date(entry.start_date).toLocaleDateString()}</>
                                    )}
                                    {entry.planned_end_date && (
                                        <> | <strong>Planned End:</strong> {new Date(entry.planned_end_date).toLocaleDateString()}</>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </Dialog>

            {/* Bulk Operation Loading Overlay */}
            {bulkOperationInProgress && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-8 rounded-lg shadow-xl max-w-md w-full mx-4">
                        <div className="flex items-center space-x-4">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600"></div>
                            <div className="flex-1">
                                <h3 className="text-xl font-semibold text-gray-900">Operation in Progress</h3>
                                <p className="text-gray-600 mt-1">{bulkOperationMessage}</p>
                                <p className="text-sm text-gray-500 mt-2">Please wait while we process your request...</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </> /* end activeTab === 'records' */}

        {/* ── Tab: License Server Config ── */}
        {activeTab === 'serverConfig' && (
            <div className="px-6 pt-4">
                <div className="flex items-center justify-between mb-4">
                    <h2 className={useNew ? 'text-lg font-semibold text-ink-primary' : 'text-lg font-semibold text-gray-800'}>License Server Configuration</h2>
                    <div className="flex gap-2">
                        <button
                            onClick={() => { const servers = [...licenseServers]; setLicenseServers([]); setTimeout(() => setLicenseServers(servers), 0); }}
                            className={useNew
                                ? 'px-3 py-2 text-sm bg-surface text-ink-primary border border-border-subtle rounded-lg hover:bg-surface-raised transition-colors'
                                : 'px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-lg transition-colors'
                            }
                            title="Refresh"
                        >
                            ↺ Refresh
                        </button>
                        <button
                            onClick={openAddLicServerDialog}
                            className={useNew
                                ? 'px-4 py-2 text-sm bg-accent hover:bg-accent-hover text-white rounded-lg font-medium transition-colors'
                                : 'px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors'
                            }
                        >
                            + Add License Server
                        </button>
                    </div>
                </div>

                <div className={useNew
                    ? 'overflow-x-auto rounded-lg border-2 border-border-strong'
                    : 'overflow-x-auto rounded-lg border border-gray-200 shadow-sm'
                }>
                    <table className="w-full text-sm">
                        <thead className={useNew ? 'bg-surface-raised border-b border-border-subtle' : 'bg-gray-50 border-b border-gray-200'}>
                            <tr>
                                <th className={useNew ? 'text-left px-4 py-3 font-medium text-ink-secondary' : 'text-left px-4 py-3 font-medium text-gray-700'}>FQDN</th>
                                <th className={useNew ? 'text-left px-4 py-3 font-medium text-ink-secondary' : 'text-left px-4 py-3 font-medium text-gray-700'}>lmgrd Port</th>
                                <th className={useNew ? 'text-left px-4 py-3 font-medium text-ink-secondary' : 'text-left px-4 py-3 font-medium text-gray-700'}>snpslmd Port</th>
                                <th className={useNew ? 'text-left px-4 py-3 font-medium text-ink-secondary' : 'text-left px-4 py-3 font-medium text-gray-700'}>SSH Port</th>
                                <th className={useNew ? 'text-left px-4 py-3 font-medium text-ink-secondary' : 'text-left px-4 py-3 font-medium text-gray-700'}>SSH User</th>
                                <th className={useNew ? 'text-left px-4 py-3 font-medium text-ink-secondary' : 'text-left px-4 py-3 font-medium text-gray-700'}>Type</th>
                                <th className={useNew ? 'text-center px-4 py-3 font-medium text-ink-secondary' : 'text-center px-4 py-3 font-medium text-gray-700'}>Main Lic File</th>
                                <th className={useNew ? 'text-center px-4 py-3 font-medium text-ink-secondary' : 'text-center px-4 py-3 font-medium text-gray-700'}>snpslmd Binary</th>
                                <th className={useNew ? 'text-center px-4 py-3 font-medium text-ink-secondary' : 'text-center px-4 py-3 font-medium text-gray-700'}>Actions</th>
                            </tr>
                        </thead>
                        <tbody className={useNew ? 'divide-y divide-border-subtle' : 'divide-y divide-gray-100'}>
                            {licenseServers.length === 0 ? (
                                <tr><td colSpan={9} className={useNew ? 'text-center py-8 text-ink-secondary' : 'text-center py-8 text-gray-500'}>No license servers configured.</td></tr>
                            ) : licenseServers.map((server) => (
                                <tr key={server.id} className={useNew ? 'hover:bg-surface-raised' : 'hover:bg-gray-50'}>
                                    <td className={useNew ? 'px-4 py-3 font-mono text-xs text-ink-primary' : 'px-4 py-3 font-mono text-xs text-gray-800'}>{server.serverFqdn}</td>
                                    <td className={useNew ? 'px-4 py-3 text-ink-primary' : 'px-4 py-3 text-gray-700'}>{server.servicePort}</td>
                                    <td className={useNew ? 'px-4 py-3 text-ink-primary' : 'px-4 py-3 text-gray-700'}>{server.vendorDaemonPort ?? <span className={useNew ? 'text-ink-secondary italic' : 'text-gray-400 italic'}>not set</span>}</td>
                                    <td className={useNew ? 'px-4 py-3 text-ink-primary' : 'px-4 py-3 text-gray-700'}>{server.sshPort}</td>
                                    <td className="px-4 py-3 font-mono text-xs">{server.adminUserid}</td>
                                    <td className="px-4 py-3"><span className={`px-2 py-1 rounded text-xs font-medium ${server.serverType === 'HostBased'
                                        ? useNew ? 'bg-accent/10 text-accent' : 'bg-blue-100 text-blue-800'
                                        : useNew ? 'bg-info/10 text-info' : 'bg-purple-100 text-purple-800'
                                    }`}>{server.serverType}</span></td>
                                    <td className="px-4 py-3 text-center">
                                        {server.mainLicFilePath
                                            ? <span title={server.mainLicFilePath} className={useNew ? 'text-success font-bold text-base cursor-default' : 'text-green-600 font-bold text-base cursor-default'}>✓</span>
                                            : <span className={useNew ? 'text-ink-secondary text-base' : 'text-gray-400 text-base'}>✗</span>}
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                        {server.snpslmdBinPath
                                            ? <span title={server.snpslmdBinPath} className={useNew ? 'text-success font-bold text-base cursor-default' : 'text-green-600 font-bold text-base cursor-default'}>✓</span>
                                            : <span className={useNew ? 'text-ink-secondary text-base' : 'text-gray-400 text-base'}>✗</span>}
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                        <div className="flex justify-center gap-2">
                                            <button
                                                onClick={() => openEditLicServerDialog(server)}
                                                className={useNew
                                                    ? 'px-2 py-1 text-xs bg-warning/10 hover:bg-warning/20 text-warning border border-warning/30 rounded transition-colors'
                                                    : 'px-2 py-1 text-xs bg-yellow-100 hover:bg-yellow-200 text-yellow-800 border border-yellow-300 rounded transition-colors'
                                                }
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => confirmDeleteLicServer(server)}
                                                className={useNew
                                                    ? 'px-2 py-1 text-xs bg-danger/10 hover:bg-danger/20 text-danger border border-danger/30 rounded transition-colors'
                                                    : 'px-2 py-1 text-xs bg-red-100 hover:bg-red-200 text-red-700 border border-red-300 rounded transition-colors'
                                                }
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Add / Edit Dialog */}
                <Dialog
                    header={editingServer ? `Edit License Server — ${editingServer.serverFqdn}` : 'Add License Server'}
                    visible={licServerDialogVisible}
                    onHide={() => setLicServerDialogVisible(false)}
                    style={{ width: '640px', maxWidth: '95vw' }}
                    className="p-fluid"
                    footer={
                        <div className="flex justify-end gap-2 pt-2">
                            <button onClick={() => setLicServerDialogVisible(false)} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">Cancel</button>
                            <button onClick={saveLicServer} disabled={serverConfigLoading} className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium disabled:opacity-50 transition-colors">
                                {serverConfigLoading ? 'Saving…' : 'Save'}
                            </button>
                        </div>
                    }
                >
                    <div className="space-y-5 pt-2">
                        <div>
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Core Connectivity</p>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="col-span-2">
                                    <label className="block text-xs font-medium text-gray-700 mb-1">FQDN <span className="text-red-500">*</span></label>
                                    <input type="text" value={licServerForm.serverFqdn} onChange={e => setLicServerForm(f => ({ ...f, serverFqdn: e.target.value }))} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="ls01.trg.vlsiexpert.in" />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">SSH Port</label>
                                    <input type="number" value={licServerForm.sshPort} onChange={e => setLicServerForm(f => ({ ...f, sshPort: parseInt(e.target.value) || 22 }))} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">SSH User</label>
                                    <input type="text" value={licServerForm.adminUserid} onChange={e => setLicServerForm(f => ({ ...f, adminUserid: e.target.value }))} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                                </div>
                                <div className="col-span-2">
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Server Type</label>
                                    <div className="flex gap-4 mt-1">
                                        {(['HostBased', 'UserBased'] as const).map(t => (
                                            <label key={t} className="flex items-center gap-2 cursor-pointer text-sm">
                                                <input type="radio" name="serverType" value={t} checked={licServerForm.serverType === t} onChange={() => setLicServerForm(f => ({ ...f, serverType: t }))} />
                                                {t}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div>
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">FlexLM Ports</p>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">lmgrd Port <span className="text-red-500">*</span></label>
                                    <input type="number" value={licServerForm.servicePort}
                                        onChange={e => {
                                            const p = parseInt(e.target.value) || 27020;
                                            setLicServerForm(f => ({ ...f, servicePort: p, vendorDaemonPort: f.vendorDaemonPort === f.servicePort + 1 ? p + 1 : f.vendorDaemonPort }));
                                        }}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">snpslmd Port</label>
                                    <input type="number" value={licServerForm.vendorDaemonPort} onChange={e => setLicServerForm(f => ({ ...f, vendorDaemonPort: parseInt(e.target.value) || (f.servicePort + 1) }))} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                                    <p className="text-xs text-gray-400 mt-1">Default: lmgrd port + 1</p>
                                </div>
                            </div>
                        </div>

                        <div>
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">File Paths</p>
                            <div className="space-y-3">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Main License File Path <span className="text-red-500">*</span></label>
                                    <input type="text" value={licServerForm.mainLicFilePath} onChange={e => setLicServerForm(f => ({ ...f, mainLicFilePath: e.target.value }))} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono" placeholder="/usr/synopsys/site-info/site-info.txt" />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Opt File Path <span className="text-red-500">*</span></label>
                                    <input type="text" value={licServerForm.licOptFilePath} onChange={e => setLicServerForm(f => ({ ...f, licOptFilePath: e.target.value }))} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono" placeholder="/usr/synopsys/site-info/snpslmd.opt" />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">snpslmd Binary Path</label>
                                    <input type="text" value={licServerForm.snpslmdBinPath} onChange={e => setLicServerForm(f => ({ ...f, snpslmdBinPath: e.target.value }))} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono" placeholder="/usr/synopsys/scl/2024.06/linux64/bin/snpslmd" />
                                    <p className="text-xs text-gray-400 mt-1">Update this path when the Synopsys SCL version changes. lmreread is derived from the same directory.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </Dialog>

                {/* Delete Confirmation Dialog */}
                <Dialog
                    header="Delete License Server"
                    visible={deleteServerDialogVisible}
                    onHide={() => setDeleteServerDialogVisible(false)}
                    style={{ width: '460px' }}
                    footer={
                        <div className="flex justify-end gap-2 pt-2">
                            <button onClick={() => setDeleteServerDialogVisible(false)} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">Cancel</button>
                            <button onClick={executeSoftDeleteLicServer} disabled={serverConfigLoading} className="px-4 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium disabled:opacity-50 transition-colors">
                                {serverConfigLoading ? 'Deleting…' : 'Delete'}
                            </button>
                        </div>
                    }
                >
                    <div className="py-2">
                        <p className="text-gray-700">Are you sure you want to delete this license server?</p>
                        {serverToDelete && (
                            <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm font-mono text-gray-800">
                                {serverToDelete.serverFqdn}:{serverToDelete.servicePort}
                            </div>
                        )}
                        <p className="mt-3 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                            The server record is soft-deleted — historical license transaction records that reference it are preserved.
                        </p>
                    </div>
                </Dialog>
            </div>
        )}

        {/* ── Tab: License File Update ── */}
        {activeTab === 'licUpdate' && (
            <div className={`px-6 pt-4 max-w-4xl ${useNew ? 'font-plex' : ''}`}>
                <h2 className={useNew ? 'text-lg font-semibold text-ink-primary mb-4' : 'text-lg font-semibold text-gray-800 mb-4'}>License File Update</h2>

                {/* Step 1: Target Server */}
                <div className={useNew
                    ? 'bg-surface border-2 border-border-strong rounded-lg p-4 mb-4 shadow-sm border-l-4 border-l-accent'
                    : 'bg-white border border-gray-200 rounded-lg p-4 mb-4 shadow-sm'}>
                    <p className={useNew
                        ? 'text-xs font-semibold text-accent uppercase tracking-wide mb-3'
                        : 'text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3'}>1. Target Server</p>
                    <div className="flex items-start gap-4 flex-wrap">
                        <div className="flex-1 min-w-48">
                            <Dropdown
                                value={licFileServer}
                                options={licenseServers.map(s => ({ label: `${s.serverFqdn}:${s.servicePort}`, value: s.id.toString() }))}
                                onChange={e => {
                                    setLicFileServer(e.value);
                                    if (uploadedFileContent) setLicFileState('ready');
                                }}
                                placeholder="Select license server"
                                style={{ width: '100%' }}
                            />
                        </div>
                        {licFileServer && (() => {
                            const srv = licenseServers.find(s => s.id.toString() === licFileServer);
                            return srv ? (
                                <div className={`flex flex-col gap-1 text-xs ${useNew ? 'text-ink-secondary' : 'text-gray-600'}`}>
                                    {srv.mainLicFilePath
                                        ? <span>Main lic file: <span className={useNew ? 'font-mono text-ink-primary' : 'font-mono text-gray-800'}>{srv.mainLicFilePath}</span></span>
                                        : <span className={useNew ? 'text-warning' : 'text-amber-600'}>⚠ mainLicFilePath not configured — configure in License Server Config tab</span>}
                                    {!srv.snpslmdBinPath && <span className={useNew ? 'text-warning' : 'text-amber-600'}>⚠ snpslmdBinPath not configured</span>}
                                </div>
                            ) : null;
                        })()}
                    </div>
                </div>

                {/* Step 2: Upload File */}
                <div className={useNew
                    ? 'bg-surface border-2 border-border-strong rounded-lg p-4 mb-4 shadow-sm border-l-4 border-l-info'
                    : 'bg-white border border-gray-200 rounded-lg p-4 mb-4 shadow-sm'}>
                    <p className={useNew
                        ? 'text-xs font-semibold text-info uppercase tracking-wide mb-3'
                        : 'text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3'}>2. Upload License File</p>
                    <label className={useNew
                        ? 'flex flex-col items-center justify-center w-full h-28 border-2 border-dashed border-border-strong rounded-lg cursor-pointer hover:border-accent hover:bg-accent/5 transition-colors'
                        : 'flex flex-col items-center justify-center w-full h-28 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors'}>
                        <div className="text-center">
                            <p className={useNew ? 'text-sm text-ink-secondary' : 'text-sm text-gray-500'}>
                                Drag & drop .lic / .txt file here, or{' '}
                                <span className={useNew ? 'text-accent font-medium' : 'text-blue-600 font-medium'}>click to browse</span>
                            </p>
                            {uploadedFileName && (
                                <p className={useNew ? 'text-xs text-ink-primary mt-1 font-medium' : 'text-xs text-gray-700 mt-1 font-medium'}>
                                    {uploadedFileName} ({uploadedFileContent?.split('\n').length} lines)
                                </p>
                            )}
                        </div>
                        <input type="file" accept=".lic,.txt" onChange={handleFileUpload} className="hidden" />
                    </label>
                </div>

                {/* Step 3: Process */}
                <div className="mb-4">
                    <button
                        onClick={handleProcessFile}
                        disabled={!licFileServer || !uploadedFileContent || licFileState === 'processing' || licFileState === 'applying'}
                        className={`px-5 py-2 rounded-lg font-medium text-sm transition-colors ${
                            licFileServer && uploadedFileContent && licFileState !== 'processing' && licFileState !== 'applying'
                                ? useNew
                                    ? 'bg-accent hover:bg-accent-hover text-white'
                                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                                : useNew
                                    ? 'bg-surface-raised text-ink-secondary border border-border-subtle cursor-not-allowed'
                                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        }`}
                    >
                        {licFileState === 'processing' ? '⟳ Processing…' : 'Process File'}
                    </button>
                    {(!licFileServer || !uploadedFileContent) && (
                        <span className={useNew ? 'ml-3 text-xs text-ink-secondary' : 'ml-3 text-xs text-gray-400'}>Select a server and upload a file first</span>
                    )}
                </div>

                {/* Processed Output Preview */}
                {(licFileState === 'preview' || licFileState === 'result' || licFileState === 'applying') && processedContent && (() => {
                    const allLines = processedContent.split('\n');
                    const CONTEXT = 3;
                    const getContext = (lineNumber: number) => {
                        const start = Math.max(0, lineNumber - 1 - CONTEXT);
                        const end = Math.min(allLines.length - 1, lineNumber - 1 + CONTEXT);
                        return allLines.slice(start, end + 1).map((line, idx) => ({
                            lineNum: start + idx + 1,
                            content: line,
                            isPatched: start + idx + 1 === lineNumber,
                        }));
                    };
                    return (
                        <div className="mb-4 space-y-3">
                            {/* Changes panel */}
                            <div className="bg-white border border-green-200 rounded-lg p-4 shadow-sm">
                                <div className="flex items-center justify-between mb-3">
                                    <p className="text-xs font-semibold text-green-700 uppercase tracking-wide">
                                        Changes Applied ({patchedLines.length} line{patchedLines.length !== 1 ? 's' : ''} patched)
                                    </p>
                                    <span className="text-xs text-gray-400">Total lines: {allLines.length}</span>
                                </div>
                                {actualHostname && (
                                    <div className="mb-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
                                        Actual hostname fetched via SSH: <span className="font-mono font-bold">{actualHostname}</span>
                                        <span className="ml-2 text-blue-500">(used in SERVER line instead of tunnel FQDN)</span>
                                    </div>
                                )}
                                {patchedLines.length === 0 ? (
                                    <p className="text-xs text-amber-600">No SERVER or VENDOR snpslmd lines found in uploaded file.</p>
                                ) : (
                                    <div className="space-y-3">
                                        {patchedLines.map(pl => (
                                            <div key={pl.lineNumber}>
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className={`text-xs font-bold px-2 py-0.5 rounded ${pl.type === 'SERVER' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                                                        {pl.type}
                                                    </span>
                                                    <span className="text-xs text-gray-500">line {pl.lineNumber}</span>
                                                </div>
                                                <pre className="text-xs font-mono bg-gray-950 rounded overflow-x-auto">
                                                    {getContext(pl.lineNumber).map(l => (
                                                        <div
                                                            key={l.lineNum}
                                                            className={l.isPatched ? 'bg-green-900 text-green-300' : 'text-gray-500'}
                                                        >
                                                            <span className="inline-block w-8 text-right pr-2 select-none opacity-50">{l.lineNum}</span>
                                                            <span className={l.isPatched ? 'text-green-300' : 'text-gray-400'}>{l.content}</span>
                                                        </div>
                                                    ))}
                                                </pre>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Full file panel */}
                            <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Full Processed File</p>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => { navigator.clipboard.writeText(processedContent); eventBus.emit('show-toast', { summary: 'Copied', message: 'Processed content copied to clipboard.', severity: 'success' }); }}
                                            className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded transition-colors"
                                        >
                                            Copy
                                        </button>
                                        <button onClick={resetLicFileUpdate} className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded transition-colors">
                                            Clear
                                        </button>
                                    </div>
                                </div>
                                <pre className="text-xs font-mono bg-gray-900 text-green-400 rounded-lg p-4 overflow-auto max-h-80 whitespace-pre">
                                    {processedContent}
                                </pre>
                            </div>
                        </div>
                    );
                })()}

                {/* Apply warning + button */}
                {licFileState === 'preview' && (
                    <div className="mb-4">
                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 mb-3">
                            {(() => {
                                    const srv = licenseServers.find(s => s.id.toString() === licFileServer);
                                    const tunnelStr = srv ? `${srv.serverFqdn}:${srv.sshPort}` : '';
                                    return (
                                        <>
                                            ⚠ Applying will overwrite the live license file on{' '}
                                            <strong>{actualHostname || srv?.serverFqdn}</strong>
                                            {actualHostname && tunnelStr && (
                                                <span className="font-normal"> (via {tunnelStr})</span>
                                            )}{' '}
                                            and trigger lmreread. This affects all active tool sessions on this server.
                                        </>
                                    );
                                })()}
                        </div>
                        <button
                            onClick={() => setApplyConfirmVisible(true)}
                            className="px-5 py-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-lg font-medium text-sm transition-colors shadow-md"
                        >
                            Apply & Reread Server
                        </button>
                    </div>
                )}

                {licFileState === 'applying' && (
                    <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800 mb-4">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                        Applying license file and triggering lmreread…
                    </div>
                )}

                {/* Result Panel */}
                {licFileState === 'result' && applyResult && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                        <p className="text-sm font-semibold text-green-800 mb-2">✓ License file applied successfully</p>
                        <div className="space-y-2 text-xs font-mono">
                            <div>
                                <span className="text-green-700 font-medium">Write result:</span>
                                <span className="ml-2 text-gray-700">{applyResult.writeResult?.message}</span>
                            </div>
                            {applyResult.rereadStdout && (
                                <div>
                                    <span className="text-green-700 font-medium">lmreread output:</span>
                                    <pre className="mt-1 p-2 bg-gray-900 text-green-400 rounded text-xs whitespace-pre-wrap">{applyResult.rereadStdout}</pre>
                                </div>
                            )}
                            {applyResult.rereadStderr && (
                                <div>
                                    <span className="text-amber-700 font-medium">lmreread stderr:</span>
                                    <pre className="mt-1 p-2 bg-gray-900 text-amber-400 rounded text-xs whitespace-pre-wrap">{applyResult.rereadStderr}</pre>
                                </div>
                            )}
                        </div>
                        <button onClick={resetLicFileUpdate} className="mt-3 px-4 py-2 text-sm bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors">
                            Apply Another File
                        </button>
                    </div>
                )}

                {/* Apply confirmation dialog */}
                <Modal
                    title="Confirm: Apply License File"
                    open={applyConfirmVisible}
                    onOk={handleApplyLicenseFile}
                    onCancel={() => setApplyConfirmVisible(false)}
                    okText="Apply & Reread"
                    okButtonProps={{ danger: true }}
                >
                    <p className="text-sm text-gray-700">
                        {(() => {
                            const srv = licenseServers.find(s => s.id.toString() === licFileServer);
                            const tunnelStr = srv ? `${srv.serverFqdn}:${srv.sshPort}` : '';
                            return (
                                <>
                                    This will overwrite the live license file on{' '}
                                    <strong>{actualHostname || srv?.serverFqdn}</strong>
                                    {actualHostname && tunnelStr && (
                                        <span className="text-gray-500 font-normal"> (via {tunnelStr})</span>
                                    )}{' '}
                                    and trigger lmreread. All active tool sessions will be affected. Are you sure?
                                </>
                            );
                        })()}
                    </p>
                </Modal>
            </div>
        )}

        </div>
    );
};

export default LicenseManagementPage;
